import React, { useState, useMemo, useEffect } from 'react';
import { CheckSquare, Square, Plus, Search, Download, Loader2, AlertCircle, Sparkles, ChevronDown, Lock, Globe, Brain, Check, Copy, FileCheck, FileText, ExternalLink, RefreshCw, ArrowRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { websiteService, Website } from '@/api/websiteService';
import { queriesService, QueryTestResult } from '@/api/queriesService';
import { llmsService } from '@/api/llmsService';
import { useAuth } from '@/context/AuthContext';
import SubscriptionModal from '@/components/SubscriptionModal';
import isURL from 'validator/es/lib/isURL';

const EMPTY_WEBSITES: Website[] = [];
const EMPTY_QUERIES: QueryTestResult[] = [];
const EMPTY_QUERY_STRINGS: string[] = [];

interface GeneratedFile {
  id: string;
  url: string;
  date: string;
  content: string;
}

interface GenerationResult {
  llmsTxt: string;
  installInstructions: string;
  urlsProcessed: number;
}

const DashboardLlmsTxtPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { userSession } = useAuth();
  const isSubscribed = userSession?.subscription?.status === 'active' || userSession?.subscription?.status === 'trialing';
  const [view, setView] = useState<'history' | 'generate' | 'addWebsite' | 'success'>('history');
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newQuery, setNewQuery] = useState('');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [websiteUrlInput, setWebsiteUrlInput] = useState('');
  const [isAddingWebsite, setIsAddingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [isGeneratingQueries, setIsGeneratingQueries] = useState(false);
  const [generatedQueries, setGeneratedQueries] = useState<string[]>([]);
  const [showWebsiteDropdown, setShowWebsiteDropdown] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [addingWebsiteStep, setAddingWebsiteStep] = useState(0);
  const [generatingQueriesStep, setGeneratingQueriesStep] = useState(0);
  const [generationResult, setGenerationResult] = useState<GenerationResult | null>(null);
  const [verifyUrl, setVerifyUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [showContentPreview, setShowContentPreview] = useState(true);

  // Progress steps for adding website
  const addingWebsiteSteps = [
    { icon: Globe, label: 'Exploring your website', subtitle: 'Checking connectivity...' },
    { icon: Brain, label: 'Extracting insights', subtitle: 'Analyzing content...' },
    { icon: Sparkles, label: 'Preparing workspace', subtitle: 'Setting things up...' }
  ];

  // Progress steps for generating queries
  const generatingQueriesSteps = [
    { label: 'Exploring your website' },
    { label: 'Extracting insights' },
    { label: 'Generating queries' }
  ];

  // Cycle through steps when adding website
  useEffect(() => {
    if (isAddingWebsite) {
      setAddingWebsiteStep(0);
      const interval = setInterval(() => {
        setAddingWebsiteStep(prev => (prev < addingWebsiteSteps.length - 1 ? prev + 1 : prev));
      }, 1500);
      return () => clearInterval(interval);
    } else {
      setAddingWebsiteStep(0);
    }
  }, [isAddingWebsite]);

  // Cycle through steps when generating queries
  useEffect(() => {
    if (isGeneratingQueries) {
      setGeneratingQueriesStep(0);
      const interval = setInterval(() => {
        setGeneratingQueriesStep(prev => (prev < generatingQueriesSteps.length - 1 ? prev + 1 : prev));
      }, 1500);
      return () => clearInterval(interval);
    } else {
      setGeneratingQueriesStep(0);
    }
  }, [isGeneratingQueries]);

  const { data: websites = EMPTY_WEBSITES, isLoading: websitesLoading, isError: websitesError } = useQuery({
    queryKey: ['websites'],
    queryFn: websiteService.getUserWebsites,
  });

  const { data: queriesData = EMPTY_QUERIES, isLoading: queriesLoading } = useQuery({
    queryKey: ['queries-by-resource', selectedWebsiteId],
    queryFn: () => queriesService.getQueriesByResource(selectedWebsiteId),
    enabled: !!selectedWebsiteId,
  });

  const queries = useMemo(() => {
    if (!queriesData || queriesData.length === 0) return EMPTY_QUERY_STRINGS;
    return [...new Set(queriesData.map(q => q.query))];
  }, [queriesData]);

  useEffect(() => {
    if (websites.length > 0 && !selectedWebsiteId) {
      setSelectedWebsiteId(websites[0].id);
    }
  }, [websites, selectedWebsiteId]);

  useEffect(() => {
    setSelectedIndices(new Set(queries.map((_, i) => i)));
  }, [queries]);

  const selectedWebsite = websites.find(w => w.id === selectedWebsiteId);

  const toggleQuery = (index: number) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIndices(newSelected);
  };

  const [customQueries, setCustomQueries] = useState<string[]>([]);

  const allQueries = useMemo(() => {
    return [...queries, ...generatedQueries, ...customQueries];
  }, [queries, generatedQueries, customQueries]);

  const addQuery = () => {
    if (newQuery.trim() && !allQueries.includes(newQuery.trim())) {
      setCustomQueries(prev => [...prev, newQuery.trim()]);
      setSelectedIndices(prev => new Set([...prev, allQueries.length]));
      setNewQuery('');
    }
  };

  const filteredQueries = allQueries.filter(q =>
    q.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGenerate = async () => {
    if (!selectedWebsite) return;

    if (!isSubscribed) {
      setSubscriptionModalOpen(true);
      return;
    }

    const selectedQueryStrings = allQueries.filter((_, i) => selectedIndices.has(i));

    setIsGenerating(true);
    try {
      const result = await websiteService.generateAndSaveLlmsTxt({
        websiteId: selectedWebsite.id,
        url: selectedWebsite.url,
        selectedQueries: selectedQueryStrings,
      });
      await queryClient.invalidateQueries({ queryKey: ['websites'] });
      setGenerationResult({
        llmsTxt: result.website?.llms_txt_content || result.llmsTxt || '',
        installInstructions: result.installInstructions || `1. Download the llms.txt file\n2. Upload it to your website root directory\n3. Verify it's accessible at ${selectedWebsite.url}/llms.txt`,
        urlsProcessed: result.urlsProcessed || 1,
      });
      setVerifyUrl(`${selectedWebsite.url}/llms.txt`);
      setView('success');
    } catch (error) {
      console.error('Error generating LLMs.txt:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const validateUrl = (url: string): boolean => {
    const urlToTest = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    return isURL(urlToTest, { protocols: ['http', 'https'], require_protocol: true, require_tld: false });
  };

  const handleAddWebsite = async () => {
    setWebsiteError(null);
    let url = websiteUrlInput.trim();

    if (!url) {
      setWebsiteError('Please enter a website URL');
      return;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    if (!validateUrl(url)) {
      setWebsiteError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setIsAddingWebsite(true);
    try {
      const pingResult = await llmsService.pingUrl(url);
      if (!pingResult.reachable) {
        if (pingResult.error?.includes('timeout')) {
          setWebsiteError('Website took too long to respond. Please check if the URL is correct.');
        } else if (pingResult.status === 404) {
          setWebsiteError('Website not found. Please check if the URL is correct.');
        } else {
          setWebsiteError('Could not reach this website. Please check if the URL is correct.');
        }
        return;
      }

      const newWebsite = await websiteService.findOrCreateWebsite(url);
      await queryClient.invalidateQueries({ queryKey: ['websites'] });
      setSelectedWebsiteId(newWebsite.id);
      setWebsiteUrlInput('');
      setGeneratedQueries([]);
      setCustomQueries([]);
      setView('generate');
    } catch {
      setWebsiteError('Failed to add website. Please try again.');
    } finally {
      setIsAddingWebsite(false);
    }
  };

  const handleAutoGenerateQueries = async () => {
    if (!selectedWebsite) return;

    setIsGeneratingQueries(true);
    try {
      const result = await websiteService.generateQuerySuggestions({
        websiteId: selectedWebsite.id,
        url: selectedWebsite.url,
      });
      const suggestedQueries = result.suggestions?.suggestedQueries || [];
      const newQueries: string[] = suggestedQueries.map((q: { query: string }) => q.query);
      const uniqueNewQueries = newQueries.filter(q => !queries.includes(q) && !generatedQueries.includes(q) && !customQueries.includes(q));
      const newGeneratedQueries = [...generatedQueries, ...uniqueNewQueries];
      setGeneratedQueries(newGeneratedQueries);
      const totalCount = queries.length + newGeneratedQueries.length + customQueries.length;
      setSelectedIndices(new Set(Array.from({ length: totalCount }, (_, i) => i)));
    } catch (error) {
      console.error('Error generating queries:', error);
    } finally {
      setIsGeneratingQueries(false);
    }
  };

  const generatedFiles: GeneratedFile[] = websites
    .filter(w => w.hasLlmsTxt && w.llmsTxtContent)
    .map(w => ({
      id: w.id,
      url: w.url,
      date: new Date(w.updatedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
      content: w.llmsTxtContent || '',
    }));

  const handleDownload = (file: GeneratedFile) => {
    const blob = new Blob([file.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'llms.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (websitesLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (websitesError) {
    return (
      <div className="flex flex-col gap-10 items-center w-full py-10">
        <div className="bg-card border border-border flex flex-col gap-5 items-center justify-center p-6 rounded w-full max-w-[1080px]">
          <div className="flex flex-col items-center justify-center py-12 w-full">
            <p className="font-sans text-base text-foreground opacity-50">
              Unable to load websites
            </p>
            <p className="font-sans text-sm text-muted-foreground mt-2">
              Please try refreshing the page
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-primary rounded h-12 px-6 flex items-center justify-center hover:opacity-90 transition-opacity"
            >
              <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                Refresh Page
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (websites.length === 0) {
    return (
      <div className="flex flex-col gap-8 items-center w-full max-w-[720px] mx-auto pt-10 pb-16 px-4">
        <div className="flex flex-col gap-3 items-center text-foreground text-center w-full max-w-[640px]">
          <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
            Generate Your LLMs.txt File
          </h1>
          <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
            Create an LLMs.txt file so AI assistants say the right things about your business.
          </p>
        </div>

        <div className="bg-card border border-border rounded px-6 py-7 w-full">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                How it works
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex gap-3 items-center">
                  <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                    <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">1</p>
                  </div>
                  <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                    Enter your website URL
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                    <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">2</p>
                  </div>
                  <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                    We'll auto-generate relevant queries for your site
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                    <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">3</p>
                  </div>
                  <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                    Generate your LLMs.txt file
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                Website URL
              </p>
              <input
                type="text"
                value={websiteUrlInput}
                onChange={(e) => {
                  setWebsiteUrlInput(e.target.value);
                  setWebsiteError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddWebsite();
                  }
                }}
                placeholder="https://mywebsite.com"
                className={`border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${websiteError ? 'border-destructive focus:ring-destructive' : ''}`}
              />
              {websiteError && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {websiteError}
                </p>
              )}
              {!websiteError && (
                <p className="font-sans italic leading-[1.5] opacity-50 text-foreground text-sm text-right">
                  We'll analyze your website to generate relevant queries.
                </p>
              )}
            </div>

            <button
              onClick={handleAddWebsite}
              disabled={isAddingWebsite}
              className="bg-primary rounded flex h-14 items-center justify-center p-3 w-full hover:opacity-90 transition-opacity disabled:opacity-70"
            >
              {isAddingWebsite ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                  <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                    {addingWebsiteSteps[addingWebsiteStep].label}
                  </span>
                </div>
              ) : (
                <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                  Continue
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'history') {
    if (generatedFiles.length > 0) {
      return (
        <div className="flex flex-col gap-6 w-full max-w-[1080px] mx-auto pt-10 pb-16 px-4 md:px-10">
          <button
            onClick={() => {
              if (selectedWebsite) {
                setGeneratedQueries([]);
                setCustomQueries([]);
                setView('generate');
              } else {
                setWebsiteUrlInput('');
                setWebsiteError(null);
                setView('addWebsite');
              }
            }}
            className="w-full bg-card border border-border rounded h-14 flex items-center justify-center gap-2 hover:bg-muted/50 transition-colors"
          >
            <Plus className="w-4 h-4 text-foreground" />
            <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground">Generate New LLMS.TXT</span>
          </button>

          <div className="flex flex-col gap-4">
            <p className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-muted-foreground">
              Previous LLMS.TXT Files
            </p>
            <div className="flex flex-col">
              {generatedFiles.map((file, index) => (
                <div
                  key={file.id}
                  className={`flex items-center justify-between py-4 ${index !== generatedFiles.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <div className="flex flex-col gap-0.5">
                    <p className="font-sans font-medium text-foreground text-base">{file.url}</p>
                    <p className="font-sans text-sm text-muted-foreground">{file.date}</p>
                  </div>
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-2 hover:opacity-60 transition-opacity"
                  >
                    <Download className="w-5 h-5 text-foreground" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <SubscriptionModal
            open={subscriptionModalOpen}
            onOpenChange={setSubscriptionModalOpen}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-8 items-center w-full max-w-[720px] mx-auto pt-10 pb-16 px-4">
        <div className="flex flex-col gap-3 items-center text-foreground text-center w-full max-w-[640px]">
          <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
            Select Queries for Your LLMs.txt
          </h1>
          <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
            Choose the queries you'd like to include. These help AI assistants understand your business.
          </p>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowWebsiteDropdown(!showWebsiteDropdown)}
            className="bg-card border border-border rounded-full px-4 py-2 flex items-center gap-2 hover:border-primary/50 transition-colors"
          >
            <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">Website:</span>
            <span className="font-sans text-sm text-foreground">{selectedWebsite?.url}</span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showWebsiteDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showWebsiteDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowWebsiteDropdown(false)} />
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-card border border-border rounded shadow-lg z-20 min-w-[280px] overflow-hidden">
                {websites.length > 1 && (
                  <>
                    <p className="px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-muted-foreground border-b border-border">
                      Your Websites
                    </p>
                    {websites.map((website) => (
                      <button
                        key={website.id}
                        onClick={() => {
                          setSelectedWebsiteId(website.id);
                          setGeneratedQueries([]);
                          setCustomQueries([]);
                          setShowWebsiteDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 font-sans text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${
                          website.id === selectedWebsiteId ? 'text-primary bg-muted/30' : 'text-foreground'
                        }`}
                      >
                        {website.url}
                        {website.id === selectedWebsiteId && (
                          <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">Current</span>
                        )}
                      </button>
                    ))}
                    <div className="border-t border-border" />
                  </>
                )}
                <button
                  onClick={() => {
                    setShowWebsiteDropdown(false);
                    setWebsiteUrlInput('');
                    setWebsiteError(null);
                    setView('addWebsite');
                  }}
                  className="w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-primary hover:bg-muted/50 transition-colors"
                >
                  + Add New Website
                </button>
              </div>
            </>
          )}
        </div>

        <div className="relative bg-card border border-border rounded px-6 py-7 w-full min-h-[400px]">
          {queriesLoading ? (
            <div className="flex flex-col items-center justify-center py-12 w-full gap-3">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          ) : allQueries.length === 0 ? (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-3">
                <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                  How it works
                </p>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">1</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Auto-generate queries based on your website content
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">2</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Review and select the queries you want to include
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">3</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Generate your LLMs.txt file
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAutoGenerateQueries}
                disabled={isGeneratingQueries}
                className="bg-primary rounded flex h-14 items-center justify-center gap-2 w-full hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {isGeneratingQueries ? (
                  <>
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                    <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      {generatingQueriesSteps[generatingQueriesStep].label}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-white" />
                    <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      Auto-Generate Queries
                    </span>
                  </>
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="bg-border h-px flex-1" />
                <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">or add manually</span>
                <div className="bg-border h-px flex-1" />
              </div>

              <div className="flex flex-col gap-3">
                <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                  Add Custom Query
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newQuery}
                    onChange={(e) => setNewQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addQuery();
                      }
                    }}
                    placeholder="e.g., What is [your product]?"
                    className="flex-1 border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={addQuery}
                    disabled={!newQuery.trim()}
                    className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 text-foreground" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                    Queries ({allQueries.length})
                  </p>
                  <button
                    onClick={handleAutoGenerateQueries}
                    disabled={isGeneratingQueries}
                    className="flex items-center gap-1.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-primary hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    {isGeneratingQueries ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {generatingQueriesSteps[generatingQueriesStep].label}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Generate More
                      </>
                    )}
                  </button>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newQuery}
                    onChange={(e) => setNewQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addQuery();
                      }
                    }}
                    placeholder="Add a custom query..."
                    className="flex-1 border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={addQuery}
                    disabled={!newQuery.trim()}
                    className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 text-foreground" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {allQueries.length > 5 && (
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search queries..."
                      className="w-full border border-border rounded p-3 pl-10 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="bg-border h-px w-full" />

              <div className="flex flex-col gap-3 max-h-[320px] overflow-y-auto overflow-x-hidden">
                {filteredQueries.map((query) => {
                  const originalIndex = allQueries.indexOf(query);
                  return (
                    <button
                      key={`${query}-${originalIndex}`}
                      onClick={() => toggleQuery(originalIndex)}
                      className="flex gap-3 items-start text-left hover:bg-muted/50 rounded p-2 -mx-2 transition-colors"
                    >
                      {selectedIndices.has(originalIndex) ? (
                        <CheckSquare className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                      ) : (
                        <Square className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={2} />
                      )}
                      <span className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                        {query}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isSubscribed && (
            <>
              <div className="absolute inset-0 bg-white/50 backdrop-blur-[6px] z-10 rounded" />
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="flex flex-col gap-5 items-center max-w-[480px] px-4 text-center">
                  <div className="bg-foreground rounded-full flex items-center justify-center size-10">
                    <Lock className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex flex-col gap-3 items-center text-foreground">
                    <h2 className="font-condensed font-bold text-xl uppercase leading-tight">
                      Unlock all Features and get your business to start appearing in AI answers
                    </h2>
                    <p className="font-sans font-normal leading-[1.5] opacity-70 text-sm">
                      Unlock all results, add unlimited queries, and generate an LLMs.txt to make sure AI says the right things about your business.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubscriptionModalOpen(true)}
                    className="bg-primary h-12 px-5 py-3 rounded flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                      Unlock All
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end w-full">
          <div className="flex items-center gap-4">
            <p className="font-mono font-medium leading-[1.5] text-foreground text-xs tracking-[0.6px] uppercase opacity-50">
              {selectedIndices.size} {selectedIndices.size === 1 ? 'query' : 'queries'} selected
            </p>
            <button
              onClick={handleGenerate}
              disabled={selectedIndices.size === 0 || isGenerating}
              className="bg-primary rounded h-14 px-8 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <>
                  {!isSubscribed && <Lock className="w-4 h-4 text-white" />}
                  <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                    {!isSubscribed ? 'Unlock to Generate' : 'Generate LLMs.txt'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        <SubscriptionModal
          open={subscriptionModalOpen}
          onOpenChange={setSubscriptionModalOpen}
        />
      </div>
    );
  }

  if (view === 'success' && generationResult) {
    const handleCopyLlms = () => {
      navigator.clipboard.writeText(generationResult.llmsTxt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const handleDownloadLlms = () => {
      const blob = new Blob([generationResult.llmsTxt], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'llms.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    return (
      <div className="flex flex-col gap-8 items-center w-full max-w-[720px] mx-auto pt-10 pb-16 px-4">
        <div className="flex flex-col gap-3 items-center text-foreground text-center w-full max-w-[640px]">
          <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
            Ready to Deploy
          </h1>
          <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
            Download and install your llms.txt
          </p>
        </div>

        <div className="flex flex-col gap-6 w-full">
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded px-4 py-3">
            <Check className="h-4 w-4 text-green-600 shrink-0" />
            <p className="font-sans text-sm text-green-800">
              Successfully generated content from {generationResult.urlsProcessed} URL{generationResult.urlsProcessed !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="bg-card border border-border rounded overflow-hidden">
            <div className="flex flex-col gap-3 px-6 py-5">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                Generated llms.txt
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleCopyLlms}
                  className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-foreground" />
                      <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground">Copy</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleDownloadLlms}
                  className="flex-1 bg-primary rounded h-12 px-4 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                >
                  <Download className="w-4 h-4 text-white" />
                  <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">Download llms.txt</span>
                </button>
              </div>
            </div>

            <div className="border-t border-border">
              <button
                onClick={() => setShowContentPreview(!showContentPreview)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileCheck className="h-4 w-4" />
                  <span className="font-sans">View Content</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showContentPreview ? 'rotate-180' : ''}`} />
              </button>
              {showContentPreview && (
                <div className="px-6 pb-6">
                  <textarea
                    value={generationResult.llmsTxt}
                    readOnly
                    className="w-full h-96 font-mono text-sm bg-muted border border-border rounded p-4 resize-none focus:outline-none"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded px-6 py-5">
            <div className="flex flex-col gap-4">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                Installation
              </p>
              <div className="bg-muted border border-border rounded p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap text-foreground">
                  {generationResult.installInstructions}
                </pre>
              </div>
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded px-4 py-3">
                <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                <p className="font-sans text-sm text-blue-800">
                  After uploading, verify at: {selectedWebsite?.url}/llms.txt
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded px-6 py-5">
            <div className="flex flex-col gap-4">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                Verify Installation
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={verifyUrl}
                  onChange={(e) => setVerifyUrl(e.target.value)}
                  placeholder="https://yoursite.com/llms.txt"
                  className="flex-1 border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  onClick={() => window.open(verifyUrl, '_blank')}
                  disabled={!verifyUrl}
                  className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  <ExternalLink className="w-4 h-4 text-foreground" />
                </button>
              </div>
            </div>
          </div>

          <div className="bg-border h-px w-full" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                setGenerationResult(null);
                setView('history');
              }}
              className="bg-card border border-border rounded h-14 px-6 flex items-center justify-center gap-2 hover:opacity-80 transition-opacity"
            >
              <RefreshCw className="w-4 h-4 text-foreground" />
              <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground">Edit Queries</span>
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-primary rounded h-14 px-6 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">Go to Dashboard</span>
              <ArrowRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'addWebsite') {
    return (
      <div className="flex flex-col gap-8 items-center w-full max-w-[720px] mx-auto pt-10 pb-16 px-4">
        <div className="flex flex-col gap-3 items-center text-foreground text-center w-full max-w-[640px]">
          <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
            Add New Website
          </h1>
          <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
            Enter the URL of the website you want to generate an LLMs.txt file for.
          </p>
        </div>

        <div className="bg-card border border-border rounded px-6 py-7 w-full">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                Website URL
              </p>
              <input
                type="text"
                value={websiteUrlInput}
                onChange={(e) => {
                  setWebsiteUrlInput(e.target.value);
                  setWebsiteError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddWebsite();
                  }
                }}
                placeholder="https://mywebsite.com"
                className={`border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${websiteError ? 'border-destructive focus:ring-destructive' : ''}`}
              />
              {websiteError && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {websiteError}
                </p>
              )}
              {!websiteError && (
                <p className="font-sans italic leading-[1.5] opacity-50 text-foreground text-sm text-right">
                  We'll analyze your website to generate relevant queries.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setView('generate')}
                className="flex-1 bg-card border border-border rounded flex h-14 items-center justify-center p-3 hover:opacity-80 transition-opacity"
              >
                <span className="font-mono font-medium leading-[1.5] text-sm text-center text-foreground tracking-[0.7px] uppercase">
                  Cancel
                </span>
              </button>
              <button
                onClick={handleAddWebsite}
                disabled={isAddingWebsite}
                className="flex-1 bg-primary rounded flex h-14 items-center justify-center p-3 hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {isAddingWebsite ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 text-white animate-spin" />
                    <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      {addingWebsiteSteps[addingWebsiteStep].label}
                    </span>
                  </div>
                ) : (
                  <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                    Add Website
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 items-center w-full max-w-[720px] mx-auto pt-10 pb-16 px-4">
      <div className="flex flex-col gap-3 items-center text-foreground text-center w-full max-w-[640px]">
        <h1 className="font-condensed font-bold leading-[1.1] text-[28px] md:text-[32px] uppercase w-full">
          Select Queries for Your LLMs.txt
        </h1>
        <p className="font-sans font-normal leading-[1.5] opacity-70 text-base w-full">
          Choose the queries you'd like to include. These help AI assistants understand your business.
        </p>
      </div>

      <div className="relative">
        <button
          onClick={() => setShowWebsiteDropdown(!showWebsiteDropdown)}
          className="bg-card border border-border rounded-full px-4 py-2 flex items-center gap-2 hover:border-primary/50 transition-colors"
        >
          <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">Website:</span>
          <span className="font-sans text-sm text-foreground">{selectedWebsite?.url}</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showWebsiteDropdown ? 'rotate-180' : ''}`} />
        </button>
        {showWebsiteDropdown && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowWebsiteDropdown(false)} />
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-card border border-border rounded shadow-lg z-20 min-w-[280px] overflow-hidden">
              {websites.length > 1 && (
                <>
                  <p className="px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-muted-foreground border-b border-border">
                    Your Websites
                  </p>
                  {websites.map((website) => (
                    <button
                      key={website.id}
                      onClick={() => {
                        setSelectedWebsiteId(website.id);
                        setGeneratedQueries([]);
                        setCustomQueries([]);
                        setShowWebsiteDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 font-sans text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${
                        website.id === selectedWebsiteId ? 'text-primary bg-muted/30' : 'text-foreground'
                      }`}
                    >
                      {website.url}
                      {website.id === selectedWebsiteId && (
                        <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">Current</span>
                      )}
                    </button>
                  ))}
                  <div className="border-t border-border" />
                </>
              )}
              <button
                onClick={() => {
                  setShowWebsiteDropdown(false);
                  setWebsiteUrlInput('');
                  setWebsiteError(null);
                  setView('addWebsite');
                }}
                className="w-full text-left px-4 py-2.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-primary hover:bg-muted/50 transition-colors"
              >
                + Add New Website
              </button>
            </div>
          </>
        )}
      </div>

      <div className="relative bg-card border border-border rounded px-6 py-7 w-full min-h-[400px]">
          {queriesLoading ? (
            <div className="flex flex-col items-center justify-center py-12 w-full gap-3">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          ) : allQueries.length === 0 ? (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-3">
                <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                  How it works
                </p>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">1</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Auto-generate queries based on your website content
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">2</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Review and select the queries you want to include
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="bg-foreground rounded-full flex items-center justify-center w-4 h-4 shrink-0">
                      <p className="font-sans font-medium leading-[1.5] text-[10px] text-center text-white">3</p>
                    </div>
                    <p className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                      Generate your LLMs.txt file
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAutoGenerateQueries}
                disabled={isGeneratingQueries}
                className="bg-primary rounded flex h-14 items-center justify-center gap-2 w-full hover:opacity-90 transition-opacity disabled:opacity-70"
              >
                {isGeneratingQueries ? (
                  <>
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                    <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      {generatingQueriesSteps[generatingQueriesStep].label}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-white" />
                    <span className="font-mono font-medium leading-[1.5] text-sm text-center text-white tracking-[0.7px] uppercase">
                      Auto-Generate Queries
                    </span>
                  </>
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="bg-border h-px flex-1" />
                <span className="font-mono text-xs tracking-wide uppercase text-muted-foreground">or add manually</span>
                <div className="bg-border h-px flex-1" />
              </div>

              <div className="flex flex-col gap-3">
                <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                  Add Custom Query
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newQuery}
                    onChange={(e) => setNewQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addQuery();
                      }
                    }}
                    placeholder="e.g., What is [your product]?"
                    className="flex-1 border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={addQuery}
                    disabled={!newQuery.trim()}
                    className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 text-foreground" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                    Queries ({allQueries.length})
                  </p>
                  <button
                    onClick={handleAutoGenerateQueries}
                    disabled={isGeneratingQueries}
                    className="flex items-center gap-1.5 font-mono font-medium text-xs tracking-[0.6px] uppercase text-primary hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    {isGeneratingQueries ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {generatingQueriesSteps[generatingQueriesStep].label}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Generate More
                      </>
                    )}
                  </button>
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newQuery}
                    onChange={(e) => setNewQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addQuery();
                      }
                    }}
                    placeholder="Add a custom query..."
                    className="flex-1 border border-border rounded p-3 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={addQuery}
                    disabled={!newQuery.trim()}
                    className="bg-card border border-border rounded h-12 px-4 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4 text-foreground" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {allQueries.length > 5 && (
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search queries..."
                      className="w-full border border-border rounded p-3 pl-10 font-sans font-normal leading-[1.5] text-foreground text-base bg-card focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="bg-border h-px w-full" />

              <div className="flex flex-col gap-3 max-h-[320px] overflow-y-auto overflow-x-hidden">
                {filteredQueries.map((query) => {
                  const originalIndex = allQueries.indexOf(query);
                  return (
                    <button
                      key={`${query}-${originalIndex}`}
                      onClick={() => toggleQuery(originalIndex)}
                      className="flex gap-3 items-start text-left hover:bg-muted/50 rounded p-2 -mx-2 transition-colors"
                    >
                      {selectedIndices.has(originalIndex) ? (
                        <CheckSquare className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                      ) : (
                        <Square className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={2} />
                      )}
                      <span className="font-sans font-normal leading-[1.5] text-foreground text-sm">
                        {query}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Paywall Overlay */}
          {!isSubscribed && (
            <>
              <div className="absolute inset-0 bg-white/50 backdrop-blur-[6px] z-10 rounded" />
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="flex flex-col gap-5 items-center max-w-[480px] px-4 text-center">
                  <div className="bg-foreground rounded-full flex items-center justify-center size-10">
                    <Lock className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex flex-col gap-3 items-center text-foreground">
                    <h2 className="font-condensed font-bold text-xl uppercase leading-tight">
                      Unlock all Features and get your business to start appearing in AI answers
                    </h2>
                    <p className="font-sans font-normal leading-[1.5] opacity-70 text-sm">
                      Unlock all results, add unlimited queries, and generate an LLMs.txt to make sure AI says the right things about your business.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubscriptionModalOpen(true)}
                    className="bg-primary h-12 px-5 py-3 rounded flex items-center justify-center hover:opacity-90 transition-opacity"
                  >
                    <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                      Unlock All
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
      </div>

      <div className="flex items-center justify-between w-full">
        <button
          onClick={() => setView('history')}
          className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to history
        </button>
        <div className="flex items-center gap-4">
          <p className="font-mono font-medium leading-[1.5] text-foreground text-xs tracking-[0.6px] uppercase opacity-50">
            {selectedIndices.size} {selectedIndices.size === 1 ? 'query' : 'queries'} selected
          </p>
          <button
            onClick={handleGenerate}
            disabled={selectedIndices.size === 0 || isGenerating}
            className="bg-primary rounded h-14 px-8 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <>
                {!isSubscribed && <Lock className="w-4 h-4 text-white" />}
                <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                  {!isSubscribed ? 'Unlock to Generate' : 'Generate LLMs.txt'}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      <SubscriptionModal
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
      />
    </div>
  );
};

export default DashboardLlmsTxtPage;
